import React, { useEffect, useMemo, useState } from 'react';
import { useBoolean } from 'react-hanger';
import { useMutation, useQuery } from '@apollo/client/react';
import { CompositeFilterDescriptor } from '@progress/kendo-data-query';
import { SelectDescriptor } from '@progress/kendo-react-data-tools';
import {
    GridFilterChangeEvent,
    GridHeaderSelectionChangeEvent,
    GridSelectionChangeEvent,
} from '@progress/kendo-react-grid';
import { getOperationAST } from 'graphql/utilities';
import DraggableDialog from '@components-clean/core/draggable-dialog';
import useGridCustomCell, { INamedItem } from '@components-clean/core/grid/custom-cell';
import Grid from '@components-clean/core/grid-fe';
import { GridColumnProps } from '@components-clean/core/grid-fe/grid.interface';
import { useUnsavedChangesContext } from '@contexts-clean/unsaved-changes-context';
import style from '@pages-clean/central-monitoring/monitoring-toolkit/tabs/dialogs/style.module.scss';
import { IExtendedLibraryRiskFields } from '@pages-clean/risk-assessment/critical-to-quality-factors/critical-to-quality-factors.interface';
import { getFilteredRisksExcludeObsolete } from '@pages-clean/risk-assessment/helpers';
import { NamedItemType } from 'types/graphql-global-types';
import { useTranslation } from '@hooks/index';
import { ShowError, ShowSuccess } from '@utils/notification';
import { GET_CM2_PANEL } from '@queries/central-monitoring';
import { GET_NAMED_ITEMS_BY_TYPE } from '@queries/common';
import { getNamedItemsByType } from '@queries/generated/common.generated';
import {
    assignRisksToCmControlChart,
    assignRisksToCmControlChartVariables,
    getCompleteRisksRiskLog,
    getCompleteRisksRiskLogVariables,
} from '@queries/generated/risk-assessment.generated';
import { ASSIGN_CM_CONTROL_RISKS, GET_COMPLETE_RISKS_RISK_LOG } from '@queries/risk-assessment';

export type IChart = {
    panelRef?: string | null;
    associatedRisks?: string[] | null;
    plotTitle?: string | null;
};

interface AssignRiskToCmControlsChartProps {
    onClose: () => void;
    onSubmit: (result: string, close: boolean) => void;
    studyRef: string;
    cmChart: IChart;
}

export type ISelectedRisks = {
    id: string;
    library: boolean;
};

const COMPONENT_NAME = 'assign-risks-to-cm-chart-dialog';

const AssignRiskToCmControlsChartDialog = ({
    onClose,
    onSubmit,
    studyRef,
    cmChart,
}: AssignRiskToCmControlsChartProps) => {
    const { confirmDiscardChanges, anyComponentHasChanges, discardChanges, setComponentHasChanges } =
        useUnsavedChangesContext();

    const { t } = useTranslation('ram', { keyPrefix: 'riskLog.dialogs.assignRisksToCmContolChart' });
    const { t: tCommon } = useTranslation('common');
    const { TooltipCell, ActiveInactiveCell } = useGridCustomCell();
    const [filteredRisks, setFilteredRisks] = useState<IExtendedLibraryRiskFields[] | null>(null);
    const {
        value: clearSelectedRows,
        setTrue: setClearSelectedRowsTrue,
        setFalse: setClearSelectedRowsFalse,
    } = useBoolean(false);
    const [select, setSelect] = React.useState<SelectDescriptor>({});
    const [filterState, setFilterState] = useState<CompositeFilterDescriptor>({
        logic: 'and',
        filters: [],
    });

    const [executeAssignRisksToCmControl, { loading: assignRisksToCmControl }] = useMutation<
        assignRisksToCmControlChart,
        assignRisksToCmControlChartVariables
    >(ASSIGN_CM_CONTROL_RISKS);

    const { data: riskCategoryData } = useQuery<getNamedItemsByType>(GET_NAMED_ITEMS_BY_TYPE, {
        variables: { type: NamedItemType.RISK_CATEGORY },
    });

    const { loading, data } = useQuery<getCompleteRisksRiskLog, getCompleteRisksRiskLogVariables>(
        GET_COMPLETE_RISKS_RISK_LOG,
        {
            variables: { studyId: studyRef },
        },
    );

    const assignableRisks = useMemo(() => {
        if (loading) return [];
        return (
            data?.risks &&
            (data?.risks
                .filter((studyRisk) => !cmChart?.associatedRisks || cmChart?.associatedRisks.indexOf(studyRisk?.id) < 0)
                .map((risk) => ({
                    ...risk,
                    library: false,
                })) as unknown as IExtendedLibraryRiskFields[])
        );
    }, [data, loading]);

    const onSubmitAssign = async (close: boolean) => {
        const operationName = getOperationAST(GET_CM2_PANEL)?.name?.value;
        const selectedRows = Object.keys(select).filter((k) => select[k] === true);
        const risks: ISelectedRisks[] =
            filteredRisks
                ?.filter((r) => selectedRows.indexOf(r.id) !== -1)
                .map((risk) => ({
                    id: risk.id,
                    library: risk.library,
                })) ?? [];

        const studyRisks = risks.filter((r) => !r.library).map((r) => r.id);

        if (!cmChart) return;
        let ids = cmChart?.associatedRisks ?? [];

        ids = ids.concat(studyRisks.filter((item: string) => ids.indexOf(item) < 0).map((r) => r));

        // Make sure we don't have any duplicates
        ids = [...Array.from(new Set(ids))];

        try {
            const response = await executeAssignRisksToCmControl({
                variables: {
                    id: cmChart.panelRef,
                    riskIds: ids,
                },
                refetchQueries: [operationName!],
            });

            if (!response?.data?.assignRisksToCmControlChart) {
                ShowError(tCommon('errorMessage'));
                return;
            }
            ShowSuccess(tCommon('actionsSuccessfulToast'));
            setFilterState({
                logic: 'and',
                filters: [],
            });
            setSelect({});
            setClearSelectedRowsTrue();
            onSubmit('success', close);
        } catch {
            ShowError(tCommon('errorMessage'));
            onSubmit('failure', close);
        }
    };

    useEffect(() => {
        if (assignableRisks) {
            const filteredAssignableRisks = getFilteredRisksExcludeObsolete(assignableRisks);
            setFilteredRisks(filteredAssignableRisks.filteredRisks);
        }
    }, [assignableRisks]);

    const onCloseHandler = async () => {
        if (anyComponentHasChanges()) {
            const confirmed = await confirmDiscardChanges();
            if (!confirmed) {
                return;
            }
            discardChanges();
        }

        onClose();
    };

    const onSelectionChange = React.useCallback(
        (event: GridSelectionChangeEvent) => {
            setClearSelectedRowsFalse();
            if (event.select) setSelect(event.select);
        },
        [setClearSelectedRowsFalse],
    );

    const onHeaderSelectionChange = React.useCallback(
        (event: GridHeaderSelectionChangeEvent) => {
            setClearSelectedRowsFalse();
            if (event.select) setSelect(event.select);
        },
        [setClearSelectedRowsFalse],
    );

    const columns: GridColumnProps[] = [
        {
            id: 'checkbox',
            field: 'checkbox',
            columnType: 'checkbox',
            className: 'checkboxSelection',
            width: 40,
            customColumn: true,
            resizable: false,
            filterable: false,
        },
        {
            id: 'category',
            field: 'riskCategory',
            title: t('columns.category'),
            minWidth: 205,
            customColumn: true,
            filter: 'text',
            filterKey: 'name',
            lovData: riskCategoryData?.namedItemsByType
                ? riskCategoryData.namedItemsByType.map((item) => item?.name as string)
                : [],
            cells: {
                data: ActiveInactiveCell({
                    inactive: (dataItem: INamedItem) => dataItem?.active === false,
                    renderValue: (dataItem: INamedItem) => dataItem?.name,
                }),
            },
        },
        {
            id: 'statement',
            field: 'statement',
            title: t('columns.statement'),
            minWidth: 420,
            customColumn: true,
            filter: 'text',
            cells: {
                data: TooltipCell({
                    isCsv: false,
                    maxLines: 2,
                }),
            },
        },
        {
            width: 1,
            minWidth: 1,
            id: 'id',
            field: 'id',
            showByDefault: false,
            title: 'ID',
        },
    ];
    const dialogTitle = `${t('title')}`;
    const formActionsEnabled =
        Object.keys(select).filter((k) => select[k] === true).length > 0 && !assignRisksToCmControl;

    useEffect(() => {
        setComponentHasChanges(COMPONENT_NAME, formActionsEnabled);
    }, [formActionsEnabled]);

    return (
        <DraggableDialog
            className={style.kWindowContent}
            dataTestId="assign-risks-to-cm-controls-chart-dialog"
            title={dialogTitle}
            onClose={onCloseHandler}
            onSubmit={() => onSubmitAssign(false)}
            primaryBtnLabel={t('buttons.assign')}
            primaryBtnDisabled={!formActionsEnabled}
            onAdditionalAction={() => onSubmitAssign(true)}
            additionalBtnLabel={t('buttons.assignAndClose')}
            additionalBtnDisabled={!formActionsEnabled}
            initialHeight={520}
            initialWidth={722}
            height={null}
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }} className="k-w-full k-h-full">
                <div>
                    <span className="k-font-medium">{t('descriptionLabel')} - </span>{' '}
                    <span className={style.textcolor}>{cmChart.plotTitle}</span>
                </div>
                <div style={{ height: '300px', paddingBottom: '0.25rem' }}>
                    <Grid
                        excludeHiddenRowMenuActions
                        title={t('title')}
                        maxHeight="auto"
                        testDataId="cm-controls-risks-grid"
                        data={filteredRisks}
                        columns={columns}
                        filterable
                        filter={filterState}
                        loading={assignRisksToCmControl}
                        useLoadingOverlay
                        showAddIcon={false}
                        scrollable="virtual"
                        dataItemKey="id"
                        selectable={{
                            enabled: true,
                            drag: false,
                            cell: false,
                            mode: 'multiple',
                        }}
                        onHeaderSelectionChange={onHeaderSelectionChange}
                        onSelectionChange={onSelectionChange}
                        clearGridSelectedRows={clearSelectedRows}
                        useCardWrapper={false}
                        onFilterChange={async (e: GridFilterChangeEvent) => {
                            const next = (e.filter || { logic: 'and', filters: [] }) as CompositeFilterDescriptor;
                            setFilterState(next);
                            setClearSelectedRowsTrue();
                            setSelect({});
                            return true;
                        }}
                        clearFilter={async () => {
                            setFilterState({ logic: 'and', filters: [] });
                            setClearSelectedRowsTrue();
                            setSelect({});
                            return true;
                        }}
                    />
                </div>
            </div>
        </DraggableDialog>
    );
};

export default AssignRiskToCmControlsChartDialog;
